/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { bind, BindingScope, config, inject } from "@loopback/core";
import { DataSet } from "vis-data";
import AsyncStorage from "@react-native-community/async-storage";
import { times } from "lodash";
import jwt_decode from "jwt-decode";
import { VApplication } from "../../application";
import { StorageModule } from "@vlinder-web/storage-module-react";

const SERVICE_NAME = "auth.service";

export const AUTH_TOKEN = "@vlinder:auth-token";
export const ACTOR = "@vlinder:actor";
export const USER_PROFILE = "@vlinder:user-profile";
export const GRAPHQL_URL = "@vlinder: graphql-url";
export const ORG_ID = "@vlinder:org-id";

@bind({ scope: BindingScope.SINGLETON })
export class AuthService {
    name = SERVICE_NAME;
    private static instance: AuthService;
    private vapp: VApplication;

    authTokenDS = new DataSet({
        fieldId: "id",
    });
    orgIdDS = new DataSet({
        fieldId: "id",
    });
    userProfileDS = new DataSet({
        fieldId: "id",
    });
    actorDS = new DataSet({
        fieldId: "id",
    });
    graphqlURLDS = new DataSet({
        fieldId: "id",
    });
    private constructor() {
        this.vapp = VApplication?.getInstance();
        this.init();
    }

    static getInstance() {
        if (!this.instance) {
            this.instance = new AuthService();
        }

        return this.instance;
    }

    async init() {
        await this.getAuthToken();
        await this.getActor();
        await this.getUserProfile();
        await this.getGraphqlURL();
        await this.getOrgId();
    }

    async setAuthToken(token: string, rememberMe : boolean) {
        try {
            if(rememberMe)
            await AsyncStorage.setItem(AUTH_TOKEN, token);
            const _authTokenDS = {
                id: AUTH_TOKEN,
                value: token,
            };
            this.authTokenDS.update(_authTokenDS);
        } catch (e) {
            // saving error
        }
    }

    async setOrgId(id: string) {
        try {
            await AsyncStorage.setItem(ORG_ID, id);
            const _orgIdDS = {
                id: ORG_ID,
                value: id,
            };
            this.orgIdDS.update(_orgIdDS);
        } catch (e) {
            // saving error
        }
    }

    async setActor(actor: string) {
        try {
            await AsyncStorage.setItem(ACTOR, actor);
            const _actorDS = {
                id: ACTOR,
                value: actor,
            };
            this.actorDS.update(_actorDS);
        } catch (e) {
            // saving error
        }
    }

    async setUserProfile(userProfile: any) {
        console.log("set user profile services", userProfile);
        try {
            let _userProfile = userProfile;
            if (userProfile && typeof userProfile !== "string") {
                _userProfile = JSON.stringify(userProfile);
            }
            await AsyncStorage.setItem(USER_PROFILE, _userProfile);
            const _userProfileDS = {
                id: USER_PROFILE,
                value: userProfile,
            };
            this.userProfileDS.update(_userProfileDS);
        } catch (e) {
            // saving error
        }
    }

    async getActor() {
        try {
            const value = await AsyncStorage.getItem(ACTOR);
            console.log("inside get actorservice", value);
            if (value !== null) {
                // value previously stored
                const _actorDS = {
                    id: ACTOR,
                    value: value,
                };
                this.actorDS.update(_actorDS);
                return value;
            } else {
                const _value = this.actorDS.get();
                console.log("actor inside else", _value);
                let temp = _value.find((item) => {
                    return item?.value;
                });
                return temp?.value;
            }
        } catch (e) {
            // error reading value
        }
    }
    async getUserProfile() {
        try {
            const value = await AsyncStorage.getItem(USER_PROFILE);
            console.log("get user profile", value);
            if (value !== null) {
                // value previously stored
                const _userProfileDS = {
                    id: USER_PROFILE,
                    value: JSON.parse(value),
                };
                this.userProfileDS.update(_userProfileDS);
                return JSON.parse(value);
            }
        } catch (e) {
            // error reading value
        }
    }

    async getAuthToken() {
        try {
            const value = await AsyncStorage.getItem(AUTH_TOKEN);
            if (value !== null) {
                // value previously stored
                const _authTokenDS = {
                    id: AUTH_TOKEN,
                    value: value,
                };
                this.authTokenDS.update(_authTokenDS);
                return value;
            }
        } catch (e) {
            // error reading value
        }
    }

    async getOrgId() {
        try {
            const value = await AsyncStorage.getItem(ORG_ID);
            if (value !== null) {
                // value previously stored
                const _orgIdDS = {
                    id: ORG_ID,
                    value: value,
                };
                this.orgIdDS.update(_orgIdDS);
                return value;
            }
        } catch (e) {
            // error reading value
        }
    }

    async setGraphqlURL(url: string) {
        console.log(`in setGraphqlURL`, url);
        try {
            await AsyncStorage.setItem(GRAPHQL_URL, url);
            const _graphqlDS = {
                id: GRAPHQL_URL,
                value: url,
            };
            this.graphqlURLDS.update(_graphqlDS);
        } catch (e) {
            // saving error
        }
    }

    async getGraphqlURL() {
        try {
            const value = await AsyncStorage.getItem(GRAPHQL_URL);
            if (value !== null) {
                const _graphqlDS = {
                    id: GRAPHQL_URL,
                    value: value,
                };
                this.graphqlURLDS.update(_graphqlDS);
                return value;
            }
        } catch (e) {
            // error reading value
        }
    }

    async removeAuthToken() {
        try {
            this.authTokenDS.clear();
            await AsyncStorage.removeItem(AUTH_TOKEN);
        } catch (e) {
            // remove error
        }
    }

    async removeOrgId() {
        try {
            this.orgIdDS.clear();
            await AsyncStorage.removeItem(ORG_ID);
        } catch (e) {
            // remove error
        }
    }

    async removeActor() {
        try {
            this.actorDS.clear();
            await AsyncStorage.removeItem(ACTOR);
        } catch (e) {
            // remove error
        }
    }

    async removeGraphqlURL() {
        try {
            this.graphqlURLDS.clear();
            await AsyncStorage.removeItem(GRAPHQL_URL);
        } catch (e) {
            // remove error
        }
    }

    async removeUserProfile() {
        try {
            this.userProfileDS.clear();
            await AsyncStorage.removeItem(USER_PROFILE);
        } catch (e) {
            // remove error
        }
    }

    async signIn({
        newToken,
        rememberMe,
        actorName,
        userProfile,
        orgId,
    }: {
        newToken: string;
        rememberMe?: boolean;
        actorName?: string;
        userProfile?: any;
        orgId?: string;
    }) {
        console.log('inside user sign in', rememberMe);
        if (newToken) {
            await this.setAuthToken(newToken, rememberMe);
        }
        if (actorName) {
            await this.setActor(actorName);
        }
        if (userProfile) {
            console.log("inside user sign in set profile 1", userProfile);
            await this.setUserProfile(userProfile);
        }
        if (orgId) {
            await this.setOrgId(orgId);
        }
    }

    async signOut() {
        const storageSrv = StorageModule.getInstance();
        storageSrv.reset("session");
        storageSrv.reset("local");
        //----------------------------//
        await this.removeAuthToken();
        await this.removeActor();
        await this.removeUserProfile();
        await this.removeGraphqlURL();
        await this.removeOrgId();
    }

    isAuthenticated() {
        const storageSrv = StorageModule.getInstance();
        //const token = this.authTokenDS.get(AUTH_TOKEN)?.value;
        const localToken = storageSrv?.get('loginLocalToken', 'local');
        const sessionToken = storageSrv?.get('loginSessionToken', 'session');

        console.log(`storageService token :`, localToken, sessionToken)

        const token = this.authTokenDS.get(AUTH_TOKEN)?.value ?? sessionToken;

        if (token) {
            let decodedToken: any = jwt_decode(token);
            let currentDate = new Date();

            //check role here
            console.log(`isAuthenticated decodedToken : `, token, decodedToken, decodedToken?.exp)

            if (decodedToken?.exp * 1000 >= currentDate.getTime()) {
                //expired
                //history.push('/')
                return true;
            } else {
                //valid
                this.signOut();
                return false;
                
            }
        } else {
            return false;
        }
    }

    getUserName() {
        const user = this.userProfileDS.get(USER_PROFILE);

        if (user) {
            console.log(`useInfo:`, user);
            return `Hello ${user?.value?.fullName}`;
        } else return `Hello`;
    }

    getToken() {
        //const token = this.authTokenDS.get(AUTH_TOKEN)?.value;
        const storageSrv = StorageModule.getInstance();
        const localToken = storageSrv?.get('loginLocalToken', 'local');
        const sessionToken = storageSrv?.get('loginSessionToken', 'session');
       const token = this.authTokenDS.get(AUTH_TOKEN)?.value ?? sessionToken;

        console.log(`storageService token in getToken:`, localToken, sessionToken)

        if (token) {
            return token;
        } else return null;
    }

    getOrganizationId() {
        const id = this.orgIdDS.get(ORG_ID);

        if (id) {
            return id?.value;
        } else return null;
    }
}
